import React, { useState, useEffect } from "react";
import apiService from "../services/api";
import "../styles/Partnerships.css";

const Partnerships = () => {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPartnership, setEditingPartnership] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    notes: "",
  });

  // Get current user permission
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isOwner = user.permission === "owner";

  useEffect(() => {
    fetchPartnerships();
  }, []);

  const fetchPartnerships = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPartnerships();
      if (response.success) {
        setPartnerships(response.partnerships);
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching partnerships:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (partnership = null) => {
    if (partnership) {
      setEditingPartnership(partnership);
      setFormData({
        name: partnership.name,
        notes: partnership.notes || "",
      });
    } else {
      setEditingPartnership(null);
      setFormData({
        name: "",
        notes: "",
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPartnership(null);
    setFormData({ name: "", notes: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Partnership name is required");
      return;
    }

    try {
      if (editingPartnership) {
        // Update existing partnership
        const response = await apiService.updatePartnership(
          editingPartnership.id,
          formData
        );
        if (response.success) {
          alert("Partnership updated successfully");
          fetchPartnerships();
          handleCloseModal();
        }
      } else {
        // Create new partnership
        const response = await apiService.createPartnership(formData);
        if (response.success) {
          alert("Partnership created successfully");
          fetchPartnerships();
          handleCloseModal();
        }
      }
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
      console.error("Error saving partnership:", err);
    }
  };

  const handleDelete = async (partnership) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${partnership.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const response = await apiService.deletePartnership(partnership.id);
      if (response.success) {
        alert("Partnership deleted successfully");
        fetchPartnerships();
      }
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
      console.error("Error deleting partnership:", err);
    }
  };

  if (loading) {
    return (
      <div className='partnerships-container'>
        <div className='loading'>Loading partnerships...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='partnerships-container'>
        <div className='error'>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className='partnerships-container'>
      <div className='partnerships-header'>
        <h1>Partnerships Management</h1>
        {isOwner && (
          <button className='btn btn-primary' onClick={() => handleOpenModal()}>
            + Add Partnership
          </button>
        )}
      </div>

      {partnerships.length === 0 ? (
        <div className='empty-state'>
          <p>No partnerships found</p>
          {isOwner && (
            <button
              className='btn btn-primary'
              onClick={() => handleOpenModal()}
            >
              Create First Partnership
            </button>
          )}
        </div>
      ) : (
        <div className='partnerships-grid'>
          {partnerships.map((partnership) => (
            <div key={partnership.id} className='partnership-card'>
              <div className='partnership-card-header'>
                <h3>{partnership.name}</h3>
                {isOwner && (
                  <div className='partnership-actions'>
                    <button
                      className='btn btn-sm btn-secondary'
                      onClick={() => handleOpenModal(partnership)}
                      title='Edit'
                    >
                      ✏️
                    </button>
                    <button
                      className='btn btn-sm btn-danger'
                      onClick={() => handleDelete(partnership)}
                      title='Delete'
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
              {partnership.notes && (
                <p className='partnership-notes'>{partnership.notes}</p>
              )}
              <div className='partnership-meta'>
                <small>
                  Created:{" "}
                  {partnership.created_at
                    ? new Date(partnership.created_at).toLocaleDateString()
                    : "N/A"}
                </small>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Create/Edit */}
      {showModal && (
        <div className='modal-overlay' onClick={handleCloseModal}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>
                {editingPartnership ? "Edit Partnership" : "New Partnership"}
              </h2>
              <button className='modal-close' onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className='form-group'>
                <label htmlFor='name'>
                  Partnership Name <span className='required'>*</span>
                </label>
                <input
                  type='text'
                  id='name'
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder='Enter partnership name'
                  required
                />
              </div>

              <div className='form-group'>
                <label htmlFor='notes'>Notes</label>
                <textarea
                  id='notes'
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder='Additional notes (optional)'
                  rows='4'
                />
              </div>

              <div className='modal-actions'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button type='submit' className='btn btn-primary'>
                  {editingPartnership ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Partnerships;
